/**
 * Rendering rules for the Ausführungsorte the detailed tender analysis produced.
 *
 * The values themselves are computed server-side (`src/tenderPerformanceLocations.ts` in
 * tri-server) and arrive with a ready-made `label`; nothing here re-assembles an address. What is
 * here is the one thing the UI has to decide on its own: **which of the three states a tender is
 * in**, because "no analysis yet" and "analysed, nothing found" are different statements and
 * showing the same dash for both is what makes a stale display look plausible.
 *
 * Pure and framework-free, so it can be unit-tested without rendering the panel.
 */

import type { TenderPerformanceLocation } from "../../TenderSearchProfileAPI";

export type PerformanceLocationsState = "not-analysed" | "none-found" | "found";

/** Structural, so the same rules serve both a `Tender` (list/detail) and a `TenderBoardRow`. */
type WithLocations = { performanceLocations?: TenderPerformanceLocation[] | null };

export function performanceLocationsState(tender: WithLocations): PerformanceLocationsState {
  const locations = tender.performanceLocations;
  // A tender stored before this feature — and every tender whose process has not reached the
  // analysis step — carries no field at all. That is not an empty result.
  if (!Array.isArray(locations)) return "not-analysed";
  return locations.length ? "found" : "none-found";
}

/** The shortest honest form of one place: the town if we know it, else whatever the label says. */
export function performanceLocationShortLabel(location: TenderPerformanceLocation): string {
  return location.city || location.name || location.label;
}

/** The tender-list cell — one place plus a count, never a truncated list that looks complete. */
export function performanceLocationsCellText(tender: WithLocations): string {
  const state = performanceLocationsState(tender);
  if (state === "not-analysed") return "—";
  if (state === "none-found") return "Keine";

  const locations = tender.performanceLocations as TenderPerformanceLocation[];
  const first = performanceLocationShortLabel(locations[0]);
  return locations.length > 1 ? `${first} +${locations.length - 1}` : first;
}

/** What the detail view says when there is nothing to list. Null while there is. */
export function performanceLocationsEmptyText(tender: WithLocations): string | null {
  switch (performanceLocationsState(tender)) {
    case "not-analysed":
      return "Für diese Ausschreibung liegt noch keine Detailanalyse vor — es wurden noch keine Ausführungsorte ermittelt.";
    case "none-found":
      return "Die Detailanalyse hat keine Ausführungsorte ermittelt.";
    default:
      return null;
  }
}

/**
 * The place the NOTICE itself published, to show while the analysis has produced nothing.
 *
 * Measured on the Geiger board: 612 of 714 tenders carry a `performanceCity`, so a bare "nothing
 * determined" block claimed far more ignorance than we have — the city is right there in the row
 * header. It is offered as a **fallback with its provenance named**, never merged into the analysis
 * list: the notice publishes one town, the analysis finds the actual objects (often dozens, per
 * lot), and collapsing the two would undo the distinction this whole feature rests on.
 *
 * Returns null once the analysis HAS places — a weaker source must not stand next to a stronger one
 * — and null when the notice published none. The buyer's address is deliberately not used as a
 * stand-in: it differs from the stated Erfüllungsort in about a third of TED notices.
 */
export function publishedPlaceFallback(row: WithLocations & { place?: string | null }): string | null {
  if (performanceLocationsState(row) === "found") return null;
  const place = (row.place ?? "").trim();
  return place || null;
}

/** One place as a single line: lot in front, note behind — used by the detail list and the CSV. */
export function formatPerformanceLocation(location: TenderPerformanceLocation): string {
  return [
    location.lot ? `${location.lot}: ` : "",
    location.label,
    location.note ? ` (${location.note})` : "",
  ]
    .join("")
    .trim();
}

/** The CSV column: every place, separated so a spreadsheet cell stays readable. */
export function performanceLocationsCsv(tender: WithLocations): string {
  const state = performanceLocationsState(tender);
  if (state !== "found") return "";
  return (tender.performanceLocations as TenderPerformanceLocation[])
    .map(formatPerformanceLocation)
    .join(" | ");
}
